import React, { useState, useEffect, useRef } from 'react';
import './UserSearchDropdown.css';
import { API_BASE_URL as API_URL } from '../config/apiBase';


const UserSearchDropdown = ({ onUserSelect, placeholder = "Search users...", excludeUserIds = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch users when dropdown opens
  useEffect(() => {
    if (isOpen && users.length === 0) {
      fetchUsers();
    }
  }, [isOpen]);

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      
      setFilteredUsers(
        users.filter(user => {
          const notExcluded = !excludeUserIds.includes(user.user_id);
          const hasNoEntity = user.entity_id == null; // allow only users without entity_id
          return notExcluded && hasNoEntity;
        })
      );
    } else {
      const filtered = users.filter(user => {
        const matchesSearch = 
          user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.user_id?.toString().includes(searchTerm);
        const notExcluded = !excludeUserIds.includes(user.user_id);
        const hasNoEntity = user.entity_id == null;
        return matchesSearch && notExcluded && hasNoEntity;
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users, excludeUserIds]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setIsOpen(false);
  };

  const handleConfirmSelection = () => {
    if (selectedUser) {
      onUserSelect(selectedUser);
      setSelectedUser(null);
      setSearchTerm('');
    }
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="user-search-dropdown" ref={dropdownRef}>
      <div className="user-search-input-container">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="user-search-input"
        />
        <div className="user-search-icon">🔍</div>
      </div>

      {selectedUser && (
        <div className="user-search-selected">
          <div className="user-search-selected-info">
            <div className="user-search-selected-name">
              {selectedUser.full_name || selectedUser.username}
            </div>
            <div className="user-search-selected-details">
              @{selectedUser.username} | ID: {selectedUser.user_id}
            </div>
          </div>
          <div className="user-search-selected-actions">
            <button 
              onClick={handleConfirmSelection}
              className="user-search-confirm-btn"
            >
              Add User
            </button>
            <button 
              onClick={handleClearSelection}
              className="user-search-clear-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {isOpen && (
        <div className="user-search-dropdown-menu">
          {loading && (
            <div className="user-search-loading">
              <div className="spinner"></div>
              Loading users...
            </div>
          )}

          {error && (
            <div className="user-search-error">
              {error}
            </div>
          )}

          {!loading && !error && filteredUsers.length === 0 && (
            <div className="user-search-empty">
              {searchTerm ? 'No users found matching your search' : 'No users available'}
            </div>
          )}

          {!loading && !error && filteredUsers.length > 0 && (
            <div className="user-search-results">
              {filteredUsers.map((user) => (
                <div
                  key={user.user_id}
                  className="user-search-item"
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="user-search-item-name">
                    {user.full_name || user.username}
                  </div>
                  <div className="user-search-item-details">
                    @{user.username} | ID: {user.user_id}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchDropdown;
